import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Avatar,
  Grid,
  Tabs,
  Tab,
  Divider,
  Alert,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Person,
  Edit,
  Save,
  Cancel,
  PhotoCamera,
  Email,
  Lock,
  Settings,
} from "@mui/icons-material";
import { useSnackbar } from "notistack";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getUserProfile,
  updateUserProfile,
  updateUserEmail,
  changePassword,
  updateUserAvatar,
} from "../services/userService";
import AvatarUploadManager from "../components/AvatarUploadManager";
import UserProfilePageSkeleton from "../components/UserProfilePageSkeleton";

// Tab configuration - moved outside component to prevent recreation
const TABS = [
  { label: "Personal Info", icon: <Person />, anchor: "personal" },
  { label: "Account", icon: <Settings />, anchor: "account" },
  { label: "Security", icon: <Lock />, anchor: "security" }
];

export default function UserProfilePage() {
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  
  // Personal Info Form State
  const [personalInfo, setPersonalInfo] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    bio: ''
  });
  const [personalInfoEditing, setPersonalInfoEditing] = useState(false);
  const [personalInfoSaving, setPersonalInfoSaving] = useState(false);

  // Email Form State
  const [emailEditing, setEmailEditing] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);

  // Password Form State
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordSaving, setPasswordSaving] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const navigate = useNavigate();

  // Get tab index from URL hash
  const getTabFromHash = useCallback((hash) => {
    const anchor = hash.replace('#', '');
    const tabIndex = TABS.findIndex(tab => tab.anchor === anchor);
    return tabIndex >= 0 ? tabIndex : 0;
  }, []);

  // Set initial tab from URL hash only once
  useEffect(() => {
    const tabIndex = getTabFromHash(location.hash);
    setCurrentTab(tabIndex);
  }, []); // Empty dependency array - only run once on mount

  // Load user profile
  useEffect(() => {
    loadUserProfile();
  }, []);

  const loadUserProfile = async () => {
    try {
      setLoading(true);
      const data = await getUserProfile();
      setUser(data.user);
      setPersonalInfo({
        firstName: data.user.firstName || '',
        lastName: data.user.lastName || '',
        phone: data.user.phone || '',
        bio: data.user.bio || ''
      });
      setNewEmail(data.user.email);
    } catch (error) {
      console.error('Error loading user profile:', error);
      enqueueSnackbar('Failed to load user profile', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    // Update URL hash without triggering re-render
    const anchor = TABS[newValue]?.anchor || TABS[0].anchor;
    navigate(`#${anchor}`, { replace: true });
  };

  // Personal Info handlers
  const handlePersonalInfoChange = (field, value) => {
    setPersonalInfo(prev => ({ ...prev, [field]: value }));
  };

  const handlePersonalInfoSave = async () => {
    try {
      setPersonalInfoSaving(true);
      const data = await updateUserProfile(personalInfo);
      setUser(prev => ({ ...prev, ...data.user }));
      setPersonalInfoEditing(false);
      enqueueSnackbar('Personal information updated successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error updating personal info:', error);
      enqueueSnackbar(error.response?.data?.error || 'Failed to update personal information', { variant: 'error' });
    } finally {
      setPersonalInfoSaving(false);
    }
  };

  const handlePersonalInfoCancel = () => {
    setPersonalInfo({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      phone: user.phone || '',
      bio: user.bio || ''
    });
    setPersonalInfoEditing(false);
  };

  // Email handlers
  const handleEmailSave = async () => {
    try {
      setEmailSaving(true);
      await updateUserEmail(newEmail);
      setUser(prev => ({ ...prev, email: newEmail }));
      setEmailEditing(false);
      enqueueSnackbar('Email updated successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error updating email:', error);
      enqueueSnackbar(error.response?.data?.error || 'Failed to update email', { variant: 'error' });
    } finally {
      setEmailSaving(false);
    }
  };

  const handleEmailCancel = () => {
    setNewEmail(user.email);
    setEmailEditing(false);
  };

  // Password handlers
  const handlePasswordSave = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      enqueueSnackbar('New passwords do not match', { variant: 'error' });
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      enqueueSnackbar('Password must be at least 6 characters long', { variant: 'error' });
      return;
    }

    try {
      setPasswordSaving(true);
      await changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      setPasswordDialogOpen(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      enqueueSnackbar('Password changed successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error changing password:', error);
      enqueueSnackbar(error.response?.data?.error || 'Failed to change password', { variant: 'error' });
    } finally {
      setPasswordSaving(false);
    }
  };

  // Avatar handlers
  const handleAvatarUpdate = (newAvatar) => {
    setUser(prev => ({ ...prev, avatar: newAvatar }));
    enqueueSnackbar('Avatar updated successfully', { variant: 'success' });
  };

  const handleAvatarDelete = () => {
    setUser(prev => ({ ...prev, avatar: null }));
    enqueueSnackbar('Avatar deleted successfully', { variant: 'success' });
  };

  // Memoize tab components to prevent unnecessary re-renders
  const PersonalInfoTab = useMemo(() => (
    <Box sx={{ mt: 2, display: currentTab !== 0 ? 'none' : 'block' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6">Personal Information</Typography>
        {!personalInfoEditing ? (
          <Button
            variant="outlined"
            startIcon={<Edit />}
            onClick={() => setPersonalInfoEditing(true)}
          >
            Edit
          </Button>
        ) : (
          <Box display="flex" gap={1}>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handlePersonalInfoSave}
              disabled={personalInfoSaving}
            >
              {personalInfoSaving ? 'Saving...' : 'Save'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<Cancel />}
              onClick={handlePersonalInfoCancel}
              disabled={personalInfoSaving}
            >
              Cancel
            </Button>
          </Box>
        )}
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="First Name"
            value={personalInfo.firstName}
            onChange={(e) => handlePersonalInfoChange('firstName', e.target.value)}
            disabled={!personalInfoEditing}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Last Name"
            value={personalInfo.lastName}
            onChange={(e) => handlePersonalInfoChange('lastName', e.target.value)}
            disabled={!personalInfoEditing}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Phone"
            value={personalInfo.phone}
            onChange={(e) => handlePersonalInfoChange('phone', e.target.value)}
            disabled={!personalInfoEditing}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Bio"
            multiline
            rows={4}
            value={personalInfo.bio}
            onChange={(e) => handlePersonalInfoChange('bio', e.target.value)}
            disabled={!personalInfoEditing}
            placeholder="Tell us about yourself..."
            inputProps={{ maxLength: 500 }}
            helperText={`${personalInfo.bio.length}/500 characters`}
          />
        </Grid>
      </Grid>
    </Box>
  ), [currentTab, personalInfo, personalInfoEditing, personalInfoSaving, handlePersonalInfoSave, handlePersonalInfoCancel, handlePersonalInfoChange]);

  const AccountTab = useMemo(() => (
    <Box sx={{ mt: 2, display: currentTab !== 1 ? 'none' : 'block' }}>
      <Grid container spacing={3}>
        {/* Profile Picture */}
        <Grid item xs={12}>
          <AvatarUploadManager
            currentAvatar={user?.avatar}
            onAvatarUpdate={handleAvatarUpdate}
            onAvatarDelete={handleAvatarDelete}
          />
        </Grid>

        {/* Email */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Email Address
              </Typography>
              <Box display="flex" alignItems="center" gap={2}>
                {emailEditing ? (
                  <>
                    <TextField
                      fullWidth
                      type="email"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      disabled={emailSaving}
                    />
                    <Button
                      variant="contained"
                      onClick={handleEmailSave}
                      disabled={emailSaving}
                    >
                      {emailSaving ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleEmailCancel}
                      disabled={emailSaving}
                    >
                      Cancel
                    </Button>
                  </>
                ) : (
                  <>
                    <Typography variant="body1" sx={{ flex: 1 }}>
                      {user?.email}
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<Edit />}
                      onClick={() => setEmailEditing(true)}
                    >
                      Edit
                    </Button>
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Account Info */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Account Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Username
                  </Typography>
                  <Typography variant="body1">
                    {user?.username}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">
                    Member Since
                  </Typography>
                  <Typography variant="body1">
                    {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
                  </Typography>
                </Grid>
                {user?.currentOrganization && (
                  <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      Organization
                    </Typography>
                    <Typography variant="body1">
                      {user.currentOrganization.name}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  ), [currentTab, user, emailEditing, newEmail, emailSaving, handleEmailSave, handleEmailCancel, handleAvatarUpdate, handleAvatarDelete]);

  const SecurityTab = useMemo(() => (
    <Box sx={{ mt: 2, display: currentTab !== 2 ? 'none' : 'block' }}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Keep your account secure with a strong password
          </Typography>
          <Button
            variant="outlined"
            startIcon={<Lock />}
            onClick={() => setPasswordDialogOpen(true)}
          >
            Change Password
          </Button>
        </CardContent>
      </Card>
    </Box>
  ), [currentTab]);

  if (loading) {
    return <UserProfilePageSkeleton />;
  }

  if (!user) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">Failed to load user profile</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Paper sx={{ p: 3 }} elevation={2}>
        <Typography variant="h4" gutterBottom>
          User Profile
        </Typography>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            {TABS.map((tab, index) => (
              <Tab
                key={tab.anchor}
                icon={tab.icon}
                label={tab.label}
                iconPosition="start"
              />
            ))}
          </Tabs>
        </Box>

        {/* Render all tabs but hide inactive ones */}
        {PersonalInfoTab}
        {AccountTab}
        {SecurityTab}
      </Paper>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              type="password"
              label="Current Password"
              value={passwordForm.currentPassword}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              type="password"
              label="New Password"
              value={passwordForm.newPassword}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
              sx={{ mb: 2 }}
              helperText="Minimum 6 characters"
            />
            <TextField
              fullWidth
              type="password"
              label="Confirm New Password"
              value={passwordForm.confirmPassword}
              onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handlePasswordSave}
            disabled={passwordSaving}
          >
            {passwordSaving ? 'Saving...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
} 